import React, { FC, Fragment, useMemo, useState } from 'react'
import tw from 'tailwind-styled-components'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import { Modal } from './Modal'
import { TextInput } from './Inputs/TextInput'
import { PhoneInput } from './Inputs/PhoneInput'
import { DatePicker } from './Inputs/DatePicker'
import { Button } from './Button'
import { Form } from './Forms/Form'
import { Select } from './Inputs/Select'
import {
  useCountriesQuery,
  useUpdatePersonDetailsMutation,
  UpdatePersonDetailsInput,
  CreateCompanyInput,
  FullUserFragment,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  CountryCode,
  AuctionPermissionIntent,
  useRequestAuctionPermissionMutation,
} from '@foros-fe/graphql'
import { CheckBox } from './Inputs/CheckBox'
import { setFormFieldsErrorTranslations } from '../utils'

type Props = FullUserFragment & {
  open: boolean
  onClose(): void
  intent: AuctionPermissionIntent
  auctionId?: string
  cadastralPlotId?: string
}
type FormData = Pick<
  UpdatePersonDetailsInput,
  'address' | 'firstName' | 'lastName' | 'phoneNumber' | 'dateOfBirth' | 'countryCode'
> & {
  company: CreateCompanyInput
}

const Title = tw.p`text-lg font-medium text-gray-800 -mt-4 mb-8`
const Footer = tw.div`
  flex
  justify-between
  border-top
  border-gray-400
`
const InputsContainer = tw.div`flex flex-col space-y-2 mb-8`
const Line = tw.div`flex flex-col md:flex-row space-x-0 space-y-2 md:space-y-0 md:space-x-6 lg:space-x-8`

export const OnboardingModal: FC<Props> = ({
  open = false,
  onClose,
  intent,
  auctionId,
  cadastralPlotId,
  ...user
}) => {
  const { firstName, lastName, dateOfBirth, address, phoneNumber, countryCode, company } = user
  const {
    id: companyId,
    companyName,
    legalCode,
    vatCode,
    firstName: representativeFirstName,
    lastName: representativeLastName,
    streetAddress,
  } = company ?? {}

  const { t, i18n } = useTranslation(['settings', 'enum', 'common'])
  const { data } = useCountriesQuery()
  const [updatePersonDetailsMutation] = useUpdatePersonDetailsMutation()
  const [updateCompanyMutation] = useUpdateCompanyMutation()
  const [createCompanyMutation] = useCreateCompanyMutation()
  const [requestAuctionPermissionMutation] = useRequestAuctionPermissionMutation()
  const methods = useForm<FormData>({
    defaultValues: {
      firstName,
      lastName,
      dateOfBirth,
      address,
      phoneNumber,
      countryCode: countryCode || (i18n.language.toUpperCase() as CountryCode),
      company: {
        firstName: representativeFirstName,
        lastName: representativeLastName,
        companyName,
        legalCode,
        vatCode,
        streetAddress,
      },
    },
  })
  const options = useMemo(
    () =>
      data?.countries.map(({ isoCode }) => ({
        label: t(`enum:CountryCode.${isoCode}`),
        value: isoCode,
      })) || [],
    [data, t]
  )
  const [companyMode, setCompanyMode] = useState(!!company)

  const onSubmit = async ({ company: companyInput, ...input }: FormData) => {
    if (companyMode) {
      const mutation = companyId ? updateCompanyMutation : createCompanyMutation

      mutation({ variables: { input: { ...companyInput, id: companyId } } })
    }

    const { data } = await updatePersonDetailsMutation({
      variables: { input },
    })

    const error = data?.updatePersonDetails?.error
    if (error) {
      return setFormFieldsErrorTranslations(setError, error, {
        phoneNumber: 'UpdatePersonDetailsPhoneNumberError',
      })
    }

    await requestAuctionPermissionMutation({
      variables: {
        input: {
          intent,
          auctionId,
          cadastralPlotId,
        },
      },
    })

    onClose()
  }

  const { register, control, setError } = methods

  return (
    <Modal open={open} onClose={onClose} cardClassname='md:w-[680px]'>
      <Title>{t('clientInformation')}</Title>
      <Form {...methods} onSubmit={onSubmit}>
        <InputsContainer>
          <div className='flex flex-col md:flex-row space-y-4 md:space-x-8 md:space-y-0'>
            <CheckBox
              checked={!companyMode}
              onChange={() => setCompanyMode(false)}
              name='physical'
              label={t('physical')}
            />
            <CheckBox
              checked={companyMode}
              onChange={() => setCompanyMode(true)}
              name='juridical'
              label={t('juridical')}
            />
          </div>
          {companyMode ? (
            <Fragment key='company'>
              <Line>
                <TextInput
                  label={t('representativeFirstName')}
                  {...register('company.firstName', { required: true })}
                />
                <TextInput
                  label={t('representativeLastName')}
                  {...register('company.lastName', { required: true })}
                />
              </Line>
              <Line>
                <TextInput
                  label={t('companyName')}
                  {...register('company.companyName', { required: true })}
                />
                <TextInput
                  label={t('streetAddress')}
                  {...register('company.streetAddress', { required: true })}
                />
              </Line>
              <Line>
                <TextInput
                  label={t('legalCode')}
                  {...register('company.legalCode', { required: true })}
                />
                <TextInput
                  label={t('vatCode')}
                  className='flex-1'
                  {...register('company.vatCode')}
                />
              </Line>
            </Fragment>
          ) : null}
          <Line>
            <TextInput label={t('firstName')} {...register('firstName', { required: true })} />
            <TextInput label={t('lastName')} {...register('lastName', { required: true })} />
          </Line>
          <Line>
            <Controller
              control={control}
              name='dateOfBirth'
              rules={{ required: true }}
              render={({ field }) => (
                <DatePicker label={t('dateOfBirth')} className='flex-1' {...field} />
              )}
            />
            <Controller
              control={control}
              name='phoneNumber'
              rules={{ required: true }}
              render={({ field }) => <PhoneInput label={t('common:phoneNumber')} {...field} />}
            />
          </Line>
          <Line>
            <TextInput label={t('address')} {...register('address', { required: true })} />
            <Controller
              control={control}
              name='countryCode'
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  label={t('countryCode')}
                  options={options}
                  onChange={(option) => field.onChange(option?.value)}
                  value={options.find(({ value }) => value === field.value)}
                  isSearchable
                  menuPlacement='auto'
                />
              )}
            />
          </Line>
        </InputsContainer>
        <Footer>
          <Button
            type='button'
            value='close-onboarding'
            title={t('common:close')}
            onClick={onClose}
            secondary
          />
          <Button type='submit' value='submit-onboarding' title={t('common:continue')} />
        </Footer>
      </Form>
    </Modal>
  )
}
